import { useState, useMemo } from 'react';
import type { ApiEndpoints, EngineEvent } from '@avisoma//shared';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { EngineEventsTable } from '../../components/reconstructedDocument/extractionTables/EngineEventsTable';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { SourceCard } from './SourceCard';

interface EngineEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

export function EngineEvents({ response }: EngineEventsProps) {
  const data = response.item.data?.engineEvents;
  const [inspected, setInspected] = useState<EngineEvent | null>(null);

  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    data?.pageIds.forEach((pageId, index) => map.set(pageId, index + 1));
    return map;
  }, [data?.pageIds]);

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SourceCard sources={[{ documentId: data.documentId, pageIds: data.pageIds }]} />

      <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Engine Events</h2>}>
        <DescriptionList>
          <DescriptionTerm>CSN</DescriptionTerm>
          <DescriptionDetails>{data.csn}</DescriptionDetails>

          <DescriptionTerm>TSN</DescriptionTerm>
          <DescriptionDetails>{data.tsn}</DescriptionDetails>

          <DescriptionTerm>TSLSV</DescriptionTerm>
          <DescriptionDetails>{data.tslsv}</DescriptionDetails>

          <DescriptionTerm>CSLSV</DescriptionTerm>
          <DescriptionDetails>{data.cslsv}</DescriptionDetails>

          <DescriptionTerm>ESN</DescriptionTerm>
          <DescriptionDetails>{data.esn}</DescriptionDetails>

          <DescriptionTerm>Date</DescriptionTerm>
          <DescriptionDetails>{data.date}</DescriptionDetails>

          <DescriptionTerm>Engine Type</DescriptionTerm>
          <DescriptionDetails>{data.engineType}</DescriptionDetails>

          <DescriptionTerm>Engine Events</DescriptionTerm>
          <DescriptionDetails>{data.engineEvents.length}</DescriptionDetails>
        </DescriptionList>
        {data.tslsvUncorroborated && (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/10 dark:text-amber-300">
            TSLSV/CSLSV anchored on an operator-log shop visit, not corroborated by a document
          </p>
        )}
      </Card>

      <ExtractionTableCard label="Engine Events" rows={data.engineEvents} csvTitle="Engine Events" bodyHeight="project">
        <EngineEventsTable
          rows={data.engineEvents}
          pageIdToPosition={pageIdToPosition}
          onInspect={setInspected}
        />
      </ExtractionTableCard>

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="Engine Event row"
        data={inspected}
      />
    </div>
  );
}
